/**
 * Gaia Tag Parser
 *
 * Parses self-closing <Gaia /> placeholder tags in agent markdown text,
 * replacing them with unique placeholders before markdown processing,
 * then resolving those placeholders into rendered HTML (content links, etc.)
 * after markdown-to-HTML conversion.
 *
 * Tags carry only `id` and `text` attributes. The `id` maps to a document's
 * `componentId`, and the document's `componentType` drives rendering dispatch.
 *
 * Mirrors the LaTeX extract-placeholder-replace pattern from
 * lib/services/markdown/index.js but scoped to Oracle-specific logic.
 */

import { oracleMarkdownToHtml } from '../../services/markdown'

// Placeholder prefix — uses a rare Unicode character (Ğ) to avoid collisions
const PLACEHOLDER_PREFIX = 'ĞĞ'

// Matches <Gaia ... /> (self-closing) and <Gaia ...> (non-self-closing) tags.
// The LLM produces both variants, so the trailing slash is optional.
export const GAIA_TAG_REGEX = /<Gaia\s+([^>]*?)\s*\/?>/g

// Matches individual key="value" attribute pairs
export const ATTRIBUTE_REGEX = /(\w+)="([^"]*)"/g

export interface GaiaTag {
  id: string
  text: string
}

export interface GaiaTagDict {
  [placeholder: string]: GaiaTag
}

export interface ContentLinkData {
  contentType: string
  contentId: number
  title: string
  path: string
}

export interface DocumentEntry {
  componentType: string
  contentLink?: ContentLinkData
}

export type DocumentMap = Map<string, DocumentEntry>

/**
 * Minimal document interface compatible with both subscription and query
 * document shapes. Used by buildDocumentMap to avoid coupling to a specific
 * GraphQL-generated type.
 */
export interface GaiaDocument {
  componentId?: string | null
  componentType?: string | null
  contentLink?: {
    contentType?: string | null
    contentId?: number | null
    title?: string | null
    path?: string | null
  } | null
}

interface ExtractResult {
  processedText: string
  tagDict: GaiaTagDict
}

/**
 * Parses attributes from a Gaia tag's attribute string.
 * Handles attributes in any order.
 */
export const parseAttributes = (attrString: string): Record<string, string> => {
  const attrs: Record<string, string> = {}
  let match = ATTRIBUTE_REGEX.exec(attrString)
  while (match) {
    const [, key, value] = match
    attrs[key] = value
    match = ATTRIBUTE_REGEX.exec(attrString)
  }
  return attrs
}

/**
 * Extracts <Gaia /> tags from text, replacing each with a unique placeholder.
 *
 * @param text - Raw markdown text potentially containing <Gaia /> tags
 * @returns processedText with placeholders, and tagDict mapping placeholders to parsed tag data
 */
export const extractGaiaTags = (text: string): ExtractResult => {
  const tagDict: GaiaTagDict = {}
  let counter = 0

  const processedText = text.replace(GAIA_TAG_REGEX, (_match, attrString: string) => {
    const attrs = parseAttributes(attrString)
    const id = attrs.id || ''
    const tagText = attrs.text || ''

    const key = `GAIA_${counter}`
    counter += 1

    const placeholder = `${PLACEHOLDER_PREFIX}${key}${PLACEHOLDER_PREFIX}`
    tagDict[placeholder] = { id, text: tagText }

    return placeholder
  })

  return { processedText, tagDict }
}

/**
 * Renders a content link as an anchor tag.
 * If the content link data is available, renders a clickable link.
 * Otherwise renders a pending span with fallback text.
 */
const renderContentLink = (
  tag: GaiaTag,
  contentLink?: ContentLinkData,
  sameTab = false,
): string => {
  if (contentLink) {
    const linkText = tag.text || contentLink.title
    const targetAttr = sameTab ? '' : ' target="_blank" rel="noopener noreferrer"'
    return `<a href="/${contentLink.path}" class="oracle-content-link" data-content-id="${contentLink.contentId}"${targetAttr}>${linkText}</a>`
  }

  // Fallback: content link data not available
  const fallbackText = tag.text || 'Loading...'
  return `<span class="oracle-content-link--pending">${fallbackText}</span>`
}

/**
 * Replaces Gaia tag placeholders in post-markdown HTML with rendered content.
 * Looks up each tag's document by id and dispatches rendering based on
 * the document's componentType.
 *
 * @param html - HTML string after markdown processing, containing placeholders
 * @param tagDict - Map of placeholders to parsed tag data
 * @param documentMap - Map of componentId to document entry (componentType + type-specific data)
 * @returns HTML with placeholders resolved to rendered content
 */
export const renderGaiaPlaceholders = (
  html: string,
  tagDict: GaiaTagDict,
  documentMap: DocumentMap,
  sameTab = false,
): string => {
  let result = html

  Object.entries(tagDict).forEach(([placeholder, tag]) => {
    const doc = documentMap.get(tag.id)
    let rendered: string

    switch (doc?.componentType) {
      case 'contentLink':
        rendered = renderContentLink(tag, doc.contentLink, sameTab)
        break
      default:
        // No matching document or unknown componentType: render fallback text
        rendered = tag.text || ''
        break
    }

    result = result.split(placeholder).join(rendered)
  })

  return result
}

/**
 * Builds a DocumentMap from raw GraphQL-shaped documents, filtered to only
 * include documents referenced by Gaia tags in the text.
 *
 * @param documents - Array of documents from GraphQL (subscription or query)
 * @param tagDict - Parsed Gaia tag dictionary from extractGaiaTags
 * @returns Map of componentId → DocumentEntry for placeholder resolution
 */
export const buildDocumentMap = (
  documents: GaiaDocument[] | null | undefined,
  tagDict: GaiaTagDict,
): DocumentMap => {
  const map: DocumentMap = new Map()
  if (!documents) return map

  const referencedIds = new Set(Object.values(tagDict).map((tag) => tag.id))

  documents.forEach((doc) => {
    if (doc.componentId && referencedIds.has(doc.componentId)) {
      const entry: DocumentEntry = { componentType: doc.componentType || '' }

      if (doc.componentType === 'contentLink' && doc.contentLink) {
        entry.contentLink = {
          contentType: doc.contentLink.contentType || '',
          contentId: doc.contentLink.contentId || 0,
          title: doc.contentLink.title || '',
          path: doc.contentLink.path || '',
        }
      }

      map.set(doc.componentId, entry)
    }
  })

  return map
}

/**
 * Full rendering pipeline for Oracle messages with Gaia tags.
 *
 * Encapsulates: extractGaiaTags → oracleMarkdownToHtml → buildDocumentMap → renderGaiaPlaceholders
 *
 * @param markdown - Raw markdown text potentially containing <Gaia /> tags
 * @param sameTab - When true, links open in the same tab
 * @param documents - Array of documents from GraphQL for resolving Gaia tag content links
 * @returns Rendered HTML string
 */
export const renderOracleMessageHtml = ({
  markdown,
  sameTab = false,
  documents,
}: {
  markdown: string
  sameTab?: boolean
  documents?: GaiaDocument[] | null
}): string => {
  const { processedText, tagDict } = extractGaiaTags(markdown || '')
  const htmlContent = oracleMarkdownToHtml({ markdown: processedText, sameTab })
  let htmlString = Array.isArray(htmlContent) ? htmlContent.join('') : htmlContent || ''

  if (Object.keys(tagDict).length > 0) {
    const documentMap = buildDocumentMap(documents, tagDict)
    htmlString = renderGaiaPlaceholders(htmlString, tagDict, documentMap, sameTab)
  }

  return htmlString
}
